import React, {useState} from 'react';
import classNames from 'classnames';
import {useIntl} from 'react-intl';
import {Dropdown, Option} from '../../../components/shared/nextgen/dropdown';
import PillSwitch from '../../../components/shared/pill-switch';
import {TimeTrackingImportColumnMapping} from '../../../features/time-tracking-import/types';
import {Lx} from '../../../i18n/consts';
import {MAPPING_FIELDS, MappingFieldConfig} from '../constants';
import styles from '../time-tracking-import.module.scss';

type MappingInputMode = 'column' | 'manual';

interface TimeTrackingImportMappingCardProps {
    mapping: TimeTrackingImportColumnMapping;
    availableColumns: string[];
    onUpdateMapping: (key: keyof TimeTrackingImportColumnMapping, value: string) => void;
}

const dropdownStyle = {
    width: '100%',
    marginBottom: 0,
    fontSize: '16px',
};

const manualInputStyle = {
    marginBottom: 0,
};

export default function TimeTrackingImportMappingCard({
    mapping,
    availableColumns,
    onUpdateMapping,
}: TimeTrackingImportMappingCardProps) {
    const intl = useIntl();
    const hasDetectedColumns = availableColumns.length > 0;
    const emptyOptionLabel = hasDetectedColumns
        ? intl.formatMessage({id: Lx.TimeTrackingImport.MAPPING_NOT_SELECTED})
        : intl.formatMessage({id: Lx.TimeTrackingImport.MAPPING_NO_DETECTED_COLUMNS});
    const [manualModeByField, setManualModeByField] = useState<Partial<Record<string, MappingInputMode>>>(() =>
        Object.fromEntries(
            MAPPING_FIELDS
                .filter((field): field is MappingFieldConfig & {manualValueKey: keyof TimeTrackingImportColumnMapping} =>
                    Boolean(field.manualValueKey))
                .map((field) => [field.key, mapping[field.manualValueKey].trim() ? 'manual' : 'column'])
        )
    );

    const handleModeChange = (field: MappingFieldConfig, manualValueKey: keyof TimeTrackingImportColumnMapping, mode: MappingInputMode) => {
        setManualModeByField((current) => ({...current, [field.key]: mode}));

        if (mode === 'manual') {
            onUpdateMapping(field.key, '');
        } else {
            onUpdateMapping(manualValueKey, '');
        }
    };

    return (
        <section className={styles.card}>
            <div className={styles.cardHeader}>
                <div>
                    <h3>{intl.formatMessage({id: Lx.TimeTrackingImport.MAPPING_TITLE})}</h3>
                    <p>{intl.formatMessage({id: Lx.TimeTrackingImport.MAPPING_DESCRIPTION})}</p>
                </div>
            </div>
            <div className={styles.mappingGrid}>
                {MAPPING_FIELDS.map((field) => {
                    const manualValueKey = field.manualValueKey;
                    const mode = manualValueKey ? (manualModeByField[field.key] ?? 'column') : 'column';
                    const hasManualValue = Boolean(manualValueKey && mapping[manualValueKey].trim());
                    const hasRequiredError = field.required
                        && hasDetectedColumns
                        && !mapping[field.key].trim()
                        && !hasManualValue;

                    return (
                        <div key={field.key} className={styles.mappingRow}>
                            <div className={styles.mappingMeta}>
                                <div className={styles.mappingLabel}>
                                    {intl.formatMessage({id: field.labelId})}
                                    {field.required && (
                                        <span className={styles.required}>
                                            {intl.formatMessage({id: Lx.Form.REQUIRED})}
                                        </span>
                                    )}
                                </div>
                                <div className={styles.mappingDescription}>
                                    {intl.formatMessage({id: field.descriptionId})}
                                </div>
                            </div>
                            <div className={styles.mappingControl}>
                                {manualValueKey && (
                                    <PillSwitch<MappingInputMode>
                                        className={styles.mappingModeSwitch}
                                        value={mode}
                                        onChange={(nextMode) => handleModeChange(field, manualValueKey, nextMode)}
                                        options={[
                                            {
                                                value: 'column',
                                                label: intl.formatMessage({id: Lx.TimeTrackingImport.MAPPING_EMPLOYEE_MODE_COLUMN}),
                                            },
                                            {
                                                value: 'manual',
                                                label: intl.formatMessage({id: Lx.TimeTrackingImport.MAPPING_EMPLOYEE_MODE_MANUAL}),
                                            },
                                        ]}
                                    />
                                )}
                                <div className={classNames(styles.mappingControlField, {
                                    [styles.mappingControlFieldManual]: mode === 'manual',
                                })}>
                                    {mode === 'manual' && manualValueKey ? (
                                        <div
                                            className={classNames(styles.mappingManualInputWrapper, {
                                                [styles.mappingDropdownError]: hasRequiredError,
                                            })}
                                        >
                                            <input
                                                type='text'
                                                className={styles.mappingManualInput}
                                                style={manualInputStyle}
                                                aria-invalid={hasRequiredError ? true : undefined}
                                                name={`mapping-${manualValueKey}`}
                                                value={mapping[manualValueKey]}
                                                placeholder={intl.formatMessage({id: Lx.TimeTrackingImport.MAPPING_EMPLOYEE_NAME_PLACEHOLDER})}
                                                onChange={(event) => onUpdateMapping(manualValueKey, event.target.value)}
                                            />
                                        </div>
                                    ) : (
                                        <Dropdown
                                            className={classNames({
                                                [styles.mappingDropdownError]: hasRequiredError,
                                                'dropdown-error': hasRequiredError,
                                            })}
                                            aria-invalid={hasRequiredError ? true : undefined}
                                            style={dropdownStyle}
                                            name={`mapping-${field.key}`}
                                            value={mapping[field.key]}
                                            placeholder={emptyOptionLabel}
                                            onChange={(_, value) => onUpdateMapping(field.key, (value as string) ?? '')}
                                            disabled={!hasDetectedColumns}
                                            selectedTextColor='secondary'
                                        >
                                            <Option value=''>{emptyOptionLabel}</Option>
                                            {availableColumns.map((column) => (
                                                <Option key={`${field.key}-${column}`} value={column}>
                                                    {column}
                                                </Option>
                                            ))}
                                        </Dropdown>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
